import { z } from "zod";

import {
	AssignStatus,
	InclusionFilterStatus,
	InclusionStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { BlacklistOverrideSkipSchema } from "../../Blacklist/schema/blacklist-override";

/**
 * Request schemas of the list table's bulk status mutations (extracted from `list.ts` to keep that
 * file shrinking).
 *
 * The filter fields are not decoration: with `selectType: ALL` the request carries no ids and the
 * backend re-derives the selection from exactly these filters, so a payload that omits one widens
 * the mutation to the whole bucket. Build them with `getBulkStatusScope` rather than by hand.
 */
export const ChangeStatusSchema = z.object({
	listPersonId: z.number(),
});

export const BulkChangeStatusSchema = z.object({
	listId: z.number(),
	assign: z.array(ChangeStatusSchema),
	assignStatus: z.nativeEnum(AssignStatus).optional(),
	oldAssignStatus: z.nativeEnum(AssignStatus).optional(),
	inclusionStatus: z.nativeEnum(InclusionStatus).optional(),
	// On a move out of the blacklist: also remove the company-wide entries that block the leads
	// (PREC-2548). The API applies the override rules per lead either way.
	removeCompany: z.boolean().optional(),
	selectType: z.nativeEnum(SelectType).optional(),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	// Bucket-scoped reason filters ("STATUS:filterParam") for select-all flows (PREC-3536).
	exclusionReasons: z.array(z.string()).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	signalFilterStatus: z.array(z.nativeEnum(SignalFilterStatus)).optional(),
	// A select-all covers the search's matches and the tab's statuses, as the table's rows do
	// (PREC-4441). The API ignores both for an id selection.
	searchValue: z.string().optional(),
	assignStatuses: z.array(z.nativeEnum(AssignStatus)).optional(),
	snoozedUntil: z
		.string()
		.transform((arg) => new Date(arg))
		.optional(),
	snoozed: z.boolean().optional(),
});

/**
 * What a bulk status change reports back (PREC-2548): the leads a move out of the blacklist had to
 * leave there, with the reason — an unsubscribe, a Signalist-wide entry, or a company entry the
 * user did not agree to remove.
 */
export const SkippedExclusionLeadSchema = z.object({
	listPersonId: z.number(),
	reason: z.string(),
});

export const BulkChangeStatusResultSchema = z.object({
	skippedFromBlacklist: z.array(BlacklistOverrideSkipSchema),
	// Leads an "Exclude from list" skipped as ineligible, with the reason the API refused them for
	// (PREC-4537). Defaults to none, so a web bundle newer than the API still reads every answer.
	skippedNotExcludable: z.array(SkippedExclusionLeadSchema).default([]),
});

export const AssignListViewSchema = z.object({
	listPersonId: z.number(),
	assignStatus: z.nativeEnum(AssignStatus),
});

export const BulkAssignListViewSchema = z.object({
	assign: z.array(AssignListViewSchema),
});

export type BulkChangeStatus = z.infer<typeof BulkChangeStatusSchema>;

export type BulkChangeStatusResult = z.infer<typeof BulkChangeStatusResultSchema>;

export type BulkAssignListView = z.infer<typeof BulkAssignListViewSchema>;
